import type { ReportAggregation } from "@glorify-llc/accounting-core";

export interface FundHealthRow {
  organization_id: string;
  fund_id: string;
  fund_name: string;
  fund_type: string;
  is_active: boolean;
  current_balance: number;
  avg_monthly_spend: number;
  months_of_runway: number | null;
  over_restriction_risk_flag: boolean;
  health_status: string;
}

export type FundHealthContext = {
  asOf: Date;
  funds: FundHealthRow[];
};

/**
 * Coerce a possibly-null/undefined/NaN/string-numeric value to a finite number.
 * PostgREST serialises NUMERIC columns as strings; without coercion, a single
 * unparseable upstream value poisons `totalBalance` (NaN propagates through `+`).
 */
function toFiniteNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Coerce truthiness of a Postgres BOOLEAN. PostgREST returns booleans as native
 * `true`/`false`, but legacy/seeded paths have produced the string `"false"`,
 * which is truthy in JS — so `if (flag)` would mis-classify EVERY row as at-risk.
 */
function toBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return Boolean(value);
}

/** Round to cents to suppress IEEE-754 sub-cent drift after summation. */
function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Format the months-of-runway display value. Null/NaN/non-finite all render
 * as "N/A" — Infinity (no monthly spend) is a legitimate "N/A" in this report
 * since the runway is undefined when the denominator is zero. Finite values
 * are rounded to one decimal place to avoid `132.45678` artifacts in printed
 * reports.
 */
function formatMonthsOfRunway(value: number | null): number | string {
  if (value === null || !Number.isFinite(value)) return "N/A";
  // Negative runway (over-spent) is a real signal — preserve it.
  return Math.round(value * 10) / 10;
}

/**
 * Normalize health status for stable downstream rendering — title-cased so
 * the renderer's color-coding doesn't have to match every input case.
 */
function normalizeHealthStatus(value: unknown): string {
  if (typeof value !== "string") return "Unknown";
  const trimmed = value.trim();
  if (trimmed.length === 0) return "Unknown";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

export function buildFundHealthAggregation(
  context: FundHealthContext,
): ReportAggregation {
  const funds = Array.isArray(context.funds) ? context.funds : [];

  const reportRows = funds.map((fund) => {
    const balance = roundCents(toFiniteNumber(fund.current_balance));
    const spend = roundCents(toFiniteNumber(fund.avg_monthly_spend));
    const isAtRisk = toBoolean(fund.over_restriction_risk_flag);
    return {
      id: fund.fund_id,
      label: fund.fund_name || "Unknown Fund",
      entityType: "contribution" as const,
      drilldownUrl: `/funds/${encodeURIComponent(fund.fund_id)}`,
      fundType: fund.fund_type,
      currentBalance: balance,
      avgMonthlySpend: spend,
      monthsOfRunway: formatMonthsOfRunway(fund.months_of_runway),
      healthStatus: normalizeHealthStatus(fund.health_status),
      riskFlag: isAtRisk ? "At Risk" : "OK",
    };
  });

  // Stable, locale-independent sort: at-risk first (so they jump to the top
  // for the user), then by descending balance (most material funds first),
  // then by fund name as a deterministic tiebreaker.
  reportRows.sort((a, b) => {
    const aRisk = a.riskFlag === "At Risk" ? 1 : 0;
    const bRisk = b.riskFlag === "At Risk" ? 1 : 0;
    if (aRisk !== bRisk) return bRisk - aRisk;
    if (a.currentBalance !== b.currentBalance) {
      return b.currentBalance - a.currentBalance;
    }
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    return 0;
  });

  // Single-pass aggregation. `is_active` and `over_restriction_risk_flag`
  // both go through `toBoolean` to defend against string-typed booleans
  // from upstream (e.g. seeded/migrated rows). `current_balance` goes
  // through `toFiniteNumber` so a single NaN doesn't poison the total.
  let totalBalance = 0;
  let activeCount = 0;
  let atRiskCount = 0;
  for (const fund of funds) {
    totalBalance += toFiniteNumber(fund.current_balance);
    if (toBoolean(fund.is_active)) activeCount += 1;
    if (toBoolean(fund.over_restriction_risk_flag)) atRiskCount += 1;
  }

  return {
    asOf: context.asOf,
    summary: [
      { label: "Total Fund Balance", value: roundCents(totalBalance) },
      { label: "Active Funds", value: activeCount },
      { label: "At Risk", value: atRiskCount },
    ],
    rows: reportRows,
  };
}
